import { FC, useMemo, useState } from 'react';

import { formatSettlementAmount } from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';
import {
  buildPayoutDateGroups,
  UnifiedMonthlyRow,
} from '../../utils/unified-settlement.utils';

// ─────────────────────────────────────────────
// PayoutDateSection
//
// "특정 지급일에 총 얼마가 나갔나(나갈 예정인가)" — 정산월 축과 직교하는 지급일 축.
// 같은 날 여러 정산서가 함께 이체되기도 하고(5월분 + 6월 1차), 같은 달이 다른 날로
// 갈리기도 해서(6월 1차/2차) 월별 표만으로는 이체 단위 금액이 안 보인다.
// 위 월별 표의 필터(연도·상조) 결과를 그대로 다시 묶으므로 두 표의 총액은 항상 일치한다.
// ─────────────────────────────────────────────

export interface PayoutDateSectionProps {
  /** 월별 표에 보이는 행 그대로 (필터 적용 후) */
  rows: UnifiedMonthlyRow[];
  /** 여러 상조가 섞인 화면 — 구성 내역에 상조명을 붙인다 */
  showBrand?: boolean;
  /**
   * 마리 수익금(1%) 칼럼 노출 — **admin·마리 화면에서만 true**.
   * 상조 화면(AgencyGiftSettlement*)에서는 절대 켜지 않는다.
   */
  showMariShare?: boolean;
}

/** 처음에 보여줄 지급일 수 (나머지는 '더 보기') */
const INITIAL_VISIBLE = 6;

export const PayoutDateSection: FC<PayoutDateSectionProps> = ({
  rows,
  showBrand = false,
  showMariShare = false,
}) => {
  const groups = useMemo(
    () => buildPayoutDateGroups(rows, { showBrand }),
    [rows, showBrand],
  );
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? groups : groups.slice(0, INITIAL_VISIBLE);

  if (groups.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
        <h4 className="text-sm font-semibold text-gray-700">지급일별 합계</h4>
        <p className="text-xs text-gray-400">
          같은 날 이체되는 정산서를 한 줄로 — 위 표와 같은 필터 기준
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-white text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">지급(예정)일</th>
              <th className="px-3 py-2 text-left">구분</th>
              <th className="px-3 py-2 text-right">배송형</th>
              <th className="px-3 py-2 text-right">쿠폰형</th>
              <th className="px-3 py-2 text-right">
                {showMariShare ? '상조 정산금 ①' : '합계'}
              </th>
              {showMariShare && (
                <th
                  className="px-3 py-2 text-right"
                  title="같은 날 마리에 추가로 보낼 판매가 1%(부가세 포함) 분배분"
                >
                  마리 수익금 1% ②
                </th>
              )}
              {showMariShare && (
                <th
                  className="px-3 py-2 text-right"
                  title="그 날 실제로 나가는(또는 계산서로 받는) 총액 = 상조 정산금 + 마리 수익금. 답례품은 전액 과세."
                >
                  총 지급액 ①+②
                </th>
              )}
              {/* 구성은 정산서가 많으면 길어져 금액 칼럼을 밀어낸다 — 30%로 묶고 줄바꿈 */}
              <th className="w-[30%] px-3 py-2 text-left">구성</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {visible.map((g) => (
              <tr key={g.key} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-3 py-2.5 font-medium tabular-nums text-gray-800">
                  {formatDate(g.date)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      g.paid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                    }`}
                  >
                    {g.paid ? '지급완료' : '지급예정'}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-600">
                  {g.deliveryAmount ? formatSettlementAmount(g.deliveryAmount) : '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-600">
                  {g.couponAmount ? formatSettlementAmount(g.couponAmount) : '-'}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-900 ${
                    // 마리 칼럼이 함께 보일 땐 '총 지급액'이 굵은 축
                    showMariShare ? 'font-semibold' : 'text-base font-bold'
                  }`}
                >
                  {formatSettlementAmount(g.totalAmount)}
                </td>
                {showMariShare && (
                  <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                    {g.mariShare > 0 ? (
                      <span className="font-semibold text-purple-700">
                        {formatSettlementAmount(g.mariShare)}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                )}
                {showMariShare && (
                  <td className="whitespace-nowrap px-3 py-2.5 text-right text-base font-bold tabular-nums text-gray-900">
                    {formatSettlementAmount(g.totalAmount + g.mariShare)}
                  </td>
                )}
                <td className="w-[30%] max-w-0 break-words px-3 py-2.5 text-xs text-gray-500">
                  {g.docCount}건 · {g.items.map((it) => it.label).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {groups.length > INITIAL_VISIBLE && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full border-t border-gray-200 px-3 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50"
        >
          {expanded ? '접기' : `${groups.length - INITIAL_VISIBLE}개 더 보기`}
        </button>
      )}
    </div>
  );
};
